<template>
  <div class="content">
    <div class="user-management">
      <div class="container-fluid">
        <div class="row">
          <div class="col-md-12">
            <Card>
              <template #header>
                <div class="header-actions">
                  <input v-model="searchTerm" type="text" class="search-input"
                    placeholder="Search by admin, user or activity title..." />
                </div>
              </template>

              <CustomTable ref="customTable" :loading="loadingStates.table" :columns="tableColumns" :data="filteredActivities" :resetPage.sync="resetTableFlag">
                <template v-slot:activityByUserName="{ item }">
                  {{ item.adminId ? item.activityByUserName : ( item.actionUsername || 'N/A') }}
                </template>
                <template v-slot:activityOnUserName="{ item }">
                  {{ item.activityOnUserName || 'N/A' }}
                </template>
                <template v-slot:type="{ item }">
                  {{ item.type || 'N/A' }}
                </template>
                <template v-slot:timestamp="{ item }">
                  {{ formatDate(item.timestamp) }}
                </template>

                <template v-slot:action="{ item }">
                  <button class="btn-sm btn-info action-button" @click="openActivity(item)">
                    Open
                  </button>
                </template>
              </CustomTable>
            </Card>

            <ReuseableModal :modelValue="isViewModalOpen" :xlarge="true" @update:modelValue="isViewModalOpen = $event"
              :scrollable="true" maxHeight="65vh"
              :title="`Activity Details of ${(selectedActivity && selectedActivity.type) || ''}`"
              submitButtonText="Close" @submit="closeViewModal" :hideSubmit="true">
              <div style="height: 65vh;" v-if="loadingStates.view">Loading...</div>

              <div v-else-if="selectedActivity" class="activity-detail-wrapper">
                <table class="activity-table">
                  <tr>
                    <td class="label">Admin Id</td>
                    <td>{{ selectedActivity.adminId || selectedActivity.actionUserId || 'N/A' }}</td>
                  </tr>

                  <tr>
                    <td class="label">Admin Name</td>
                    <td>{{ selectedActivity.adminId ? selectedActivity.activityByUserName : ( selectedActivity.actionUsername || 'N/A') }}</td>
                  </tr>

                  <tr>
                    <td class="label">User Id</td>
                    <td>{{ selectedActivity.activityOnUser || 'N/A' }}</td>
                  </tr>

                  <tr>
                    <td class="label">User Name</td>
                    <td>{{ selectedActivity.activityOnUserName || 'N/A' }}</td>
                  </tr>

                  <tr>
                    <td class="label">Activity</td>
                    <td>{{ selectedActivity.activityTitle || 'N/A' }}</td>
                  </tr>

                  <tr>
                    <td class="label">Description</td>
                    <td v-html="formattedDescription"></td>
                  </tr>

                  <tr>
                    <td class="label">Latitude</td>
                    <td>{{ selectedActivity.latitude || 'N/A' }}</td>
                  </tr>

                  <tr>
                    <td class="label">Longitude</td>
                    <td>{{ selectedActivity.longitude || 'N/A' }}</td>
                  </tr>

                  <tr>
                    <td class="label">Browser Name</td>
                    <td>{{ selectedActivity.browserName || 'N/A' }}</td>
                  </tr>

                  <tr>
                    <td class="label">OS Name</td>
                    <td>{{ selectedActivity.osName || 'N/A' }}</td>
                  </tr>

                  <tr>
                    <td class="label">Activity Date</td>
                    <td>{{ formatDate(selectedActivity.timestamp) || 'N/A' }}</td>
                  </tr>

                  <tr>
                    <td class="label">Changes</td>
                    <td>
                      <div v-if="selectedActivity.changes" style="white-space: pre-line;" v-html="formattedChanges">
                      </div>
                      <span v-else>N/A</span>
                    </td>
                  </tr>

                  <tr>
                    <td class="label">Location</td>
                    <td>
                      <div v-if="selectedActivity.latitude && selectedActivity.longitude" class="">
                        <iframe
                          :src="`https://maps.google.com/maps?q=${selectedActivity.latitude},${selectedActivity.longitude}&z=15&output=embed`"
                          width="100%" height="220" style="border:0;"></iframe>
                      </div>

                      <span v-else>N/A</span>
                    </td>
                  </tr>
                </table>
              </div>
            </ReuseableModal>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import Card from "src/components/UIComponents/Cards/Card.vue";
import CustomTable from "src/components/UIComponents/CustomTable.vue";
import ReuseableModal from "src/components/UIComponents/ReuseableModal.vue";
import { formatAdminActivityDate } from "@/utils/fileUtils";
import firebase from "../../../../assets/config/firebase";

export default {
  components: {
    Card,
    CustomTable,
    ReuseableModal
  },

  data() {
    return {
      searchTerm: "",
      resetTableFlag: false,
      activityList: [],
      selectedActivity: null,

      isViewModalOpen: false,

      tableColumns: [
        { label: "Admin Name", key: "activityByUserName", sortable: true, sticky: "left" },
        { label: "User Name", key: "activityOnUserName", sortable: true, sticky: "left" },
        { label: "Type", key: "type", sortable: true },
        { label: "Activity Date", key: "timestamp", sortable: true },
        { label: "Open Activity", key: "action", sticky: "right" }
      ],

      loadingStates: {
        table: false,
        view: false
      }
    };
  },

  computed: {
    formattedDescription() {
      const desc = this.selectedActivity.activityDescription;
      if (!desc) return 'N/A';

      const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

      return desc.replace(emailRegex, '<strong>$1</strong>');
    },
    formattedChanges() {
      if (!this.selectedActivity && !this.selectedActivity.changes) return '';

      return this.selectedActivity.changes
        .split('\n')
        .map(line => {
          const index = line.indexOf(':');
          if (index === -1) return line;

          const key = line.slice(0, index);
          const value = line.slice(index + 1);

          return `<strong>${key}</strong>:${value}`;
        })
        .join('\n');
    },
    filteredActivities() {
      if (!this.searchTerm) return this.activityList;
      const term = this.searchTerm.toLowerCase();

      return this.activityList.filter(a => {
        const byUser = a.activityByUserName ? a.activityByUserName.toLowerCase() : '';
        const onUser = a.activityOnUserName ? a.activityOnUserName.toLowerCase() : '';
        const type = a.type ? a.type : '';

        return byUser.includes(term) || onUser.includes(term) || type.includes(term);
      });
    },
    mapLink() {
      if (!this.latitude || !this.longitude) return null;
      return `https://www.google.com/maps?q=${this.latitude},${this.longitude}`;
    },
    googleMapEmbedUrl() {
      if (!this.latitude || !this.longitude) return '';
      return `https://www.google.com/maps/embed/v1/view?key=YOUR_GOOGLE_MAPS_API_KEY&center=${this.latitude},${this.longitude}&zoom=15`;
    }
  },

  mounted() {
    this.fetchActivities();
  },

  methods: {
    resetTablePage() {
      // emit to child table
    },
    formatDate(date) {
      return formatAdminActivityDate(date);
    },

    async fetchActivities() {
      this.loadingStates.table = true;
      try {
        this.activityList = await firebase.getAllAdminActivities();
      } catch (err) {
        console.error("Fetch admin activities failed", err);
      } finally {
        this.loadingStates.table = false;
      }
    },

    async openActivity(item) {
      this.isViewModalOpen = true;
      this.loadingStates.view = true;
      this.selectedActivity = null;

      try {
        this.selectedActivity = item;
      } catch (err) {
        console.error("Fetch activity failed", err);
      } finally {
        this.loadingStates.view = false;
      }
    },
    closeViewModal() {
      this.isViewModalOpen = false;
      this.selectedActivity = null;
    }
  },

  watch: {
    searchTerm() {
      this.resetTableFlag = true;
    }
  }
};
</script>


<style scoped>
.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.actions-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.loader-icon {
  height: 18px;
  vertical-align: middle;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.icon-btn {
  background: transparent;
  border: none;
  padding: 6px;
  cursor: pointer;
}

.add-employee[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

.d-none {
  display: none;
}

.truncate-text {
  display: inline-block;
  max-width: 320px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-detail-wrapper {
  padding: 10px 0;
}

.activity-table {
  width: 100%;
  border-collapse: collapse;
}

.activity-table td {
  padding: 12px 14px;
  border-bottom: 1px solid #eee;
  vertical-align: top;
}

.activity-table .label {
  width: 200px;
  font-weight: 600;
  color: #333;
  background: #fafafa;
}

.change-item {
  margin-bottom: 6px;
}

.map-link {
  color: #5b6cff;
  font-weight: 500;
  text-decoration: none;
}

.map-link:hover {
  text-decoration: underline;
}

.map-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.map-link {
  font-size: 12px;
  color: #1a73e8;
  text-decoration: underline;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/AdminActivities/AdminActivities.vue